import React from 'react';
import { List, ListItemButton, Tooltip } from '@mui/material';
import { AppContext, SnackContext } from 'src/store/ContextStore';
import { useLoggedInUserName } from 'src/hooks/useLoggedInUserName';
import {
  isPregnancyNode,
  isGameteDonorNode,
  canCarryPregnancy
} from '../../pedigree/config';
import {
  PregnancyModal,
  DeleteConfirm,
  AddFetusConfirm,
  usePregnancyCrud,
  spouseOptionsFor
} from './pregnancyEditor';
import {
  quickAddPregnancyPayload,
  appendFetusPayload
} from '../../pedigree/pregnancyQuickAdd';
import GameteDonorModal from './GameteDonorModal';
import { PEDIGREE } from 'src/components/Constants';

// Track B (pregnancy-record model): pregnancies + donors are RECORDS on the carrier
// (Pregnancy/PregnancyChanged), not persons or config nodes; the pedigree DERIVES the
// render nodes from them. This context-menu entry authors those records via the shared
// editor (PregnancyModal + usePregnancyCrud). Add lives on a carrier; Edit/Delete resolve
// the carrier + record behind a DERIVED pregnancy/donor node via its uid (pregnancy uid =
// record._id; donor uid = `<record._id>:<index>`).

// The record _id behind a derived node (pregnancy uid = _id; donor uid = `_id:index`).
const recordIdOf = (node) => {
  const uid = node?.uid == null ? '' : String(node.uid);
  return isGameteDonorNode(node) ? uid.split(':')[0] : uid;
};

// Resolve the carrier MEMBER (from memberRecords, the source of truth) + the pregnancy
// record for a node. For a carrier node → that member + no record (Add). For a derived
// pregnancy/donor node → the member whose pregnancies[] holds the record + the record.
const resolveCarrierAndRecord = (node, memberRecords) => {
  const list = memberRecords || [];
  if (isPregnancyNode(node) || isGameteDonorNode(node)) {
    const rid = recordIdOf(node);
    const carrier = list.find((m) =>
      (m.pregnancies || []).some((p) => String(p._id) === rid)
    );
    const record = carrier?.pregnancies?.find((p) => String(p._id) === rid);
    return { carrier, record };
  }
  const carrier = list.find((m) => m.pid === node.pid) || node;
  return { carrier, record: null };
};

export default function ReproductiveMenu({
  node,
  user,
  memberRecords,
  setMemberRecords,
  handleClose,
  setPedigreeState
}) {
  const { setSnack } = React.useContext(SnackContext);
  // See ReproductiveSection: the family's diagnoses drive colour consistency for a fetus diagnosis.
  const { content } = React.useContext(AppContext);
  const familyDiagnoses = content?.pedigreeSelectedUsers?.diagnoses || [];
  // Attribution for fetus diagnoses/phenotypes — the signed-in clinician.
  const recordedBy = useLoggedInUserName();
  const [modalOpen, setModalOpen] = React.useState(false);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [fetusConfirmOpen, setFetusConfirmOpen] = React.useState(false);
  // TGCLOUD-1613 — recording a donor against someone already born, with no pregnancy involved.
  // Holds the DIRECTION being recorded ('received' | 'donated'), or null when closed.
  const [donorModalOpen, setDonorModalOpen] = React.useState(null);
  const {
    busy,
    submitPregnancy,
    deletePregnancy,
    toggleHidden,
    convertFetusToPerson,
    createDonorPerson
  } = usePregnancyCrud({
    memberRecords,
    setMemberRecords,
    setPedigreeState,
    setSnack,
    user
  });

  const { carrier, record } = resolveCarrierAndRecord(node, memberRecords);
  const isReproNodeSelected = isPregnancyNode(node) || isGameteDonorNode(node);
  const canCarry = canCarryPregnancy(node);
  const spouseOptions = spouseOptionsFor(carrier, memberRecords);

  // Return the result; the modal closes itself via onClose (and may first prompt to add the
  // live-birth child).
  const handleSubmit = (formValues) =>
    submitPregnancy(carrier, record, formValues);

  const closeAll = () => {
    setModalOpen(false);
    handleClose?.();
  };

  const handleDelete = async () => {
    const ok = await deletePregnancy(carrier, record);
    if (ok) {
      setConfirmOpen(false);
      handleClose?.();
    }
  };

  // The one-click path: no form, just the confirmation and the record. Since TGCLOUD-1617 item 2 this
  // is reachable ONLY from an existing pregnancy's menu ("Add another fetus"), where it appends a
  // second fetus and the twin machinery draws the pair. The carrier's menu now opens the form instead,
  // so the `quickAddPregnancyPayload` branch below is a defensive fallback rather than a live path.
  const handleQuickAddFetus = async () => {
    const payload = record
      ? appendFetusPayload(record)
      : quickAddPregnancyPayload(new Date());
    const saved = await submitPregnancy(carrier, record, payload);
    if (saved) {
      setFetusConfirmOpen(false);
      handleClose?.();
    }
  };

  // TGCLOUD-1613 — the selected person as held in memberRecords (the source of truth for _id and
  // gameteDonors); the menu node is a laid-out copy.
  const targetPerson = (memberRecords || []).find(
    (m) => m.pid === (node?.pid ?? node?.data?.pid)
  );

  // Donor links are RENDER TOPOLOGY, so they persist with the pedigree config on the next save —
  // the same route bioFatherId/bioMotherId takes. No pregnancy record is written, deliberately.
  //
  // The link ALWAYS lands on the RECIPIENT, whichever direction the modal was opened in — so
  // `recipientPid` is supplied by the modal rather than assumed to be the selected person. That is
  // what lets one stored shape serve both entry points.
  const markSaving = () =>
    setPedigreeState?.((prev) => ({
      ...prev,
      save: PEDIGREE.saveStatus.IN_PROGRESS
    }));

  const handleAddLink = async ({ recipientPid, role, donorRef }) => {
    if (recipientPid == null) return;
    setMemberRecords?.((prev) =>
      (prev || []).map((m) =>
        m.pid === recipientPid
          ? {
              ...m,
              gameteDonors: [
                ...(Array.isArray(m.gameteDonors) ? m.gameteDonors : []),
                { role, ...(donorRef ? { memberId: String(donorRef) } : {}) }
              ]
            }
          : m
      )
    );
    markSaving();
    setDonorModalOpen(null);
    handleClose?.();
  };

  const handleRemoveLink = async ({ recipientPid, index }) => {
    if (recipientPid == null) return;
    setMemberRecords?.((prev) =>
      (prev || []).map((m) => {
        if (m.pid !== recipientPid) return m;
        const next = (
          Array.isArray(m.gameteDonors) ? m.gameteDonors : []
        ).filter((_, i) => i !== index);
        // Back to `undefined` when empty, matching the schema default, so a person who no longer has
        // a donor does not keep an empty array behind them.
        return { ...m, gameteDonors: next.length ? next : undefined };
      })
    );
    markSaving();
  };

  // A visible repro node can be hidden from here; it then can't be right-clicked, so
  // un-hiding is done from the carrier's Reproductive sidebar (which lists hidden records).
  const handleHide = async () => {
    const ok = await toggleHidden(carrier, record);
    if (ok) handleClose?.();
  };

  return (
    <>
      <List>
        {isReproNodeSelected ? (
          <>
            <ListItemButton onClick={() => setModalOpen(true)}>
              Edit pregnancy…
            </ListItemButton>
            {/* Only on a pregnancy — a donor node has no fetus list of its own. */}
            {isPregnancyNode(node) && (
              <ListItemButton
                onClick={() => setFetusConfirmOpen(true)}
                disabled={busy}
              >
                Add another fetus
              </ListItemButton>
            )}
            <ListItemButton onClick={handleHide} disabled={busy}>
              Hide from pedigree
            </ListItemButton>
            <ListItemButton onClick={() => setConfirmOpen(true)}>
              Delete pregnancy
            </ListItemButton>
          </>
        ) : (
          <Tooltip
            title={
              canCarry
                ? ''
                : 'Set Sex Assigned at Birth to Female before adding a pregnancy'
            }
            placement="right"
          >
            <span style={{ display: 'block' }}>
              {/* ONE way in (TGCLOUD-1617 item 2). Kathryn: "it doesn't make sense to have both
                  options… I would prefer to have it say add fetus. But I want the functionality that
                  we've got for add pregnancy." So the label is hers and the behaviour is the FORM —
                  which is also what gives her the partner default she asked for (item 3): a new
                  record seeds "conceived with" from the carrier's spouse.

                  The one-click quick-add is gone from this menu with it. That deliberately reverses
                  an earlier choice of mine — the quick-add asserted nothing about conception so that
                  nothing was assumed — because her workflow is overwhelmingly partnered, not donor.
                  "Add another fetus" survives on an existing pregnancy's own menu (Tim's decision 1),
                  since that is the only way to add a twin from the chart. */}
              <ListItemButton
                onClick={() => setModalOpen(true)}
                disabled={!canCarry}
              >
                Add fetus…
              </ListItemButton>
            </span>
          </Tooltip>
        )}
        {/* TGCLOUD-1613 — offered on ANY person, not only a gestational carrier: the point of the
            ticket is that a family seen after the children were born should not have to invent a
            pregnancy to get the donors drawn. It is the person who was donor-CONCEIVED that carries
            the link, so this sits outside the canCarry gate above. */}
        {!isReproNodeSelected && targetPerson && (
          <>
            <ListItemButton
              onClick={() => setDonorModalOpen('received')}
              disabled={busy}
            >
              Record a gamete donor for this person…
            </ListItemButton>
            {/* The reverse direction. Both were built because Tim's QA on G-1-01491 recorded two
                donors backwards through the recipient-first form alone: selecting the person you
                think of as the donor and picking the recipient stores the exact inverse, and the
                drawn line looks the same either way. */}
            <ListItemButton
              onClick={() => setDonorModalOpen('donated')}
              disabled={busy}
            >
              Record this person as a gamete donor…
            </ListItemButton>
          </>
        )}
      </List>

      {modalOpen && (
        <PregnancyModal
          record={record}
          spouseOptions={spouseOptions}
          familyDiagnoses={familyDiagnoses}
          recordedBy={recordedBy}
          carrier={carrier}
          onSubmit={handleSubmit}
          onClose={closeAll}
          onConvert={(rec, gender, fetusIndex) =>
            convertFetusToPerson(carrier, rec, gender, fetusIndex)
          }
          memberRecords={memberRecords}
          onCreateDonorPerson={createDonorPerson}
        />
      )}
      {donorModalOpen && targetPerson && (
        <GameteDonorModal
          person={targetPerson}
          memberRecords={memberRecords}
          mode={donorModalOpen}
          onAddLink={handleAddLink}
          onRemoveLink={handleRemoveLink}
          onClose={() => setDonorModalOpen(null)}
          onCreateDonorPerson={createDonorPerson}
          busy={busy}
        />
      )}
      {fetusConfirmOpen && (
        <AddFetusConfirm
          existing={!!record}
          onConfirm={handleQuickAddFetus}
          onClose={() => setFetusConfirmOpen(false)}
          saving={busy}
        />
      )}
      {confirmOpen && (
        <DeleteConfirm
          onConfirm={handleDelete}
          onClose={() => setConfirmOpen(false)}
          deleting={busy}
        />
      )}
    </>
  );
}
